import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useMonthlyUserTrends } from '../../../hooks/analytics/useUtilizationAnalytics';
import { lazy, Suspense, useMemo, memo } from 'react';
const UserTrendsChart = lazy(async () => ({
  default: (await import('./UserTrendsChart')).UserTrendsChart,
}));
import { AnalyticsCardWrapper } from './shared';
import { SkeletonLoader } from '../shared/SkeletonLoader';
import { createSafeMonthDate, formatDate } from '../utils/dataUtils';
import { palette } from '../shared/theme';

interface UserTrendsCardProps {
  filters: BaseAnalyticsFilter;
}

export const UserTrendsCard = memo(({ filters }: UserTrendsCardProps) => {
  const { data, isLoading, isError, error } = useMonthlyUserTrends(filters);

  const exportData = useMemo(() => {
    if (!data?.length) return [];

    return data.map((trend, index) => ({
      rank: index + 1,
      month: formatDate(createSafeMonthDate(trend.month_year), 'long'),
      monthYear: trend.month_year,
      newUsers: trend.new_users,
      returningUsers: trend.returning_users,
      totalUsers: trend.total_users,
      returnUserRate: `${trend.return_user_rate.toFixed(1)}%`,
    }));
  }, [data]);

  return (
    <AnalyticsCardWrapper
      title="Monthly Member Trends"
      subtitle="New vs Returning Members"
      isLoading={isLoading}
      isError={isError}
      error={error}
      isEmpty={!data?.length}
      emptyMessage="No member trend data available"
      exportData={exportData}
      exportFileName="monthly-member-trends"
      accent={palette.cyan}
    >
      <Suspense fallback={<SkeletonLoader variant="chart" height="h-[380px]" />}>
        <UserTrendsChart data={data || []} height={380} />
      </Suspense>
    </AnalyticsCardWrapper>
  );
});

UserTrendsCard.displayName = 'UserTrendsCard';
